import { useEffect, useState } from "react";
import { ClipboardList, PhoneCall, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Action, EmptyState, PageHeader, Panel, SearchField, SelectField, StatusBadge, Toolbar } from "./ui";

const STATUS_LABELS = {
  new: "Nova",
  assigned: "Atribuída",
  contact_attempted: "Tentativa de contato",
  contacted: "Contato realizado",
  assessment_scheduled: "Avaliação agendada",
  converted: "Convertida",
  closed: "Encerrada",
  duplicate: "Duplicada",
  spam: "Spam",
};
const TONES = {
  new: "pending",
  assigned: "progress",
  contact_attempted: "progress",
  contacted: "progress",
  assessment_scheduled: "done",
  converted: "done",
  closed: "neutral",
  duplicate: "neutral",
  spam: "alert",
};

export default function EvaluationRequests() {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/portal/evaluation-requests", {
        params: { status: filter.status || undefined, q: filter.q || undefined },
      });
      setItems(data);
      if (selected) setSelected(data.find((d) => d.id === selected.id) || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.status, filter.q]);

  const run = async (fn, message) => {
    try {
      await fn();
      if (message) toast.success(message);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (items === null) return <Loading label="Carregando solicitações…" />;

  return (
    <div data-testid="portal-requests-page" className="space-y-6">
      <PageHeader
        kicker="Admissões"
        title="Solicitações de avaliação"
        subtitle="Registros de contato do aplicativo. Não se misturam com prontuário ou episódio clínico: a conversão exige processo explícito."
      />

      <Toolbar testId="requests-toolbar">
        <SelectField
          testId="requests-filter-status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
        <SearchField
          testId="requests-search-input"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          placeholder="Buscar por nome, protocolo ou cidade"
          className="w-72"
        />
      </Toolbar>

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <div className="space-y-3">
          {items.length === 0 ? (
            <EmptyState
              testId="requests-empty"
              icon={ClipboardList}
              title="Nenhuma solicitação com esses filtros"
              description="Limpe a busca ou escolha outro status para ver os demais contatos recebidos pelo aplicativo."
            />
          ) : (
            items.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`request-row-${r.protocol}`}
                onClick={() => setSelected(r)}
                className={`w-full rounded-2xl border bg-white p-5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:bg-sand/40 ${
                  selected?.id === r.id ? "border-sage-600" : "border-border"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[13px] text-ink-500">{r.protocol}</span>
                  <StatusBadge tone={TONES[r.status]} testId={`request-status-${r.protocol}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </StatusBadge>
                </div>
                <p className="mt-2.5 font-display text-[16px] font-semibold text-ink-900">{r.full_name}</p>
                <p className="mt-1 text-[13.5px] text-ink-500">
                  {r.interest_topic} · {r.city}/{r.state} · {formatDate(r.created_at)}
                </p>
              </button>
            ))
          )}
        </div>

        {selected ? (
          <Panel testId="request-detail" className="space-y-5">
            <div>
              <p className="font-mono text-[14px] text-ink-500">{selected.protocol}</p>
              <h2 className="mt-1 font-display text-[21px] font-semibold text-ink-900">{selected.full_name}</h2>
              <p className="mt-2.5 text-[14.5px] leading-relaxed text-ink-700">
                {selected.phone} {selected.phone_has_whatsapp ? "(WhatsApp)" : ""} · {selected.email || "sem e-mail"} ·{" "}
                {selected.city}/{selected.state}
              </p>
              <p className="mt-1.5 text-[14px] leading-relaxed text-ink-600">
                {selected.for_self ? "Para si" : `Para outra pessoa (${selected.relationship || "relação não informada"})`} ·
                interesse: {selected.interest_topic} · canal: {selected.preferred_channel} · período:{" "}
                {selected.preferred_period}
              </p>
              {selected.message ? (
                <p className="mt-3.5 rounded-xl bg-sand/60 p-4 text-[14.5px] leading-relaxed text-ink-700">
                  {selected.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-5">
              <Action
                size="sm"
                variant="secondary"
                icon={UserCheck}
                data-testid="request-assign"
                onClick={() =>
                  run(() => api.post(`/portal/evaluation-requests/${selected.id}/assign`), "Atribuída a você")
                }
              >
                Atribuir a mim
              </Action>
              {["contacted", "assessment_scheduled", "converted", "closed", "duplicate", "spam"].map((status) => (
                <Action
                  key={status}
                  size="sm"
                  variant="secondary"
                  data-testid={`request-status-${status}`}
                  onClick={() =>
                    run(
                      () =>
                        api.post(`/portal/evaluation-requests/${selected.id}/status`, {
                          status,
                          note,
                          next_action: nextAction,
                        }),
                      `Status: ${STATUS_LABELS[status]}`
                    )
                  }
                >
                  {STATUS_LABELS[status]}
                </Action>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                data-testid="request-next-action-input"
                value={nextAction}
                onChange={(e) => setNextAction(e.target.value)}
                placeholder="Próxima ação"
                className="h-11 rounded-xl text-[14.5px]"
              />
              <Input
                data-testid="request-outcome-input"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                placeholder="Resultado da tentativa de contato"
                className="h-11 rounded-xl text-[14.5px]"
              />
            </div>
            <Textarea
              data-testid="request-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Observação interna"
              className="rounded-xl text-[14.5px]"
            />
            <Action
              icon={PhoneCall}
              data-testid="request-contact-attempt"
              disabled={outcome.trim().length < 2}
              onClick={() =>
                run(
                  () =>
                    api.post(`/portal/evaluation-requests/${selected.id}/contact-attempt`, {
                      channel: selected.preferred_channel,
                      outcome,
                      note,
                    }),
                  "Tentativa registrada"
                ).then(() => setOutcome(""))
              }
            >
              Registrar tentativa de contato
            </Action>

            <div className="border-t border-border pt-5">
              <p className="er-label text-[12px] tracking-[0.16em]">Evolução</p>
              <div className="mt-3 space-y-2">
                {(selected.timeline || []).map((entry, i) => (
                  <p key={i} data-testid={`request-timeline-${i}`} className="text-[13.5px] leading-relaxed text-ink-600">
                    {formatDate(entry.at)} — <span className="font-semibold text-ink-800">{entry.type}</span>: {entry.detail}
                    {entry.note ? ` · ${entry.note}` : ""}
                    {entry.by ? ` · ${entry.by}` : ""}
                  </p>
                ))}
              </div>
            </div>
          </Panel>
        ) : (
          <EmptyState
            testId="request-none-selected"
            icon={ClipboardList}
            title="Selecione uma solicitação"
            description="Escolha um protocolo para ver os dados de contato e a evolução do atendimento."
          />
        )}
      </div>
    </div>
  );
}
